import { useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * To determines whether a specified number of milliseconds have passed
 * since the event's Firebase creation timestamp, taking timezone into account.
 *
 * @param {number} durationMS - The number of **milliseconds** to wait after event creation.
 * @returns {[ hasElapsed: boolean, loading: boolean ]}
 */
export const useHasEnoughTimeElapsedSinceEventCreation = (durationMS: number): [hasElapsed: boolean, loading: boolean] => {
  const { eventInfo, loading } = useEventInfo();

  const createEventFirebaseDate = eventInfo?.firebaseCreateAt;

  const hasElapsed = useMemo(() => {
    if (!createEventFirebaseDate?.timestamp) return false;

    // Step 1: Parse the string into a Date object (automatically handles timezone)
    const eventDate = new Date(createEventFirebaseDate?.timestamp);
    // Step 2: Get current time
    const now = new Date();

    // Step 3: Check if durationMS has elapsed
    const zonedTimestamp = eventDate.getTime();
    const nowZonedTimestamp = now.getTime();
    const msElapsed = nowZonedTimestamp - zonedTimestamp;

    return msElapsed >= durationMS;
  }, [createEventFirebaseDate?.timestamp, durationMS]);

  return [hasElapsed, loading];
};
